// ./src/components/features/Venues/VenuesList/VenuesList.tsx
//
// Venues list — table on desktop (>= md), cards on tablet/mobile. Tap-safe
// (explicit Edit/Delete only), rights-gated per row (a public-directory venue the
// caller doesn't own is read-only and not selectable), and selection-ready.
// Mirrors the Highlights list conventions (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
  Anchor,
  Avatar,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { Venue, VenueCategory, venueLogoUrl } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalVenuesContext } from "@/contexts/VenuesContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Props
// ============================================

export interface VenuesListProps {
  data?: Venue[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditVenue?: (venue: Venue) => void;
  onDeleteVenue?: (venue: Venue) => void;
  /** #170 — opens a vendor's people and documents (Library › Vendors). */
  onOpenVenue?: (venue: Venue) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddVenue?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /** 'venue' (default) or 'vendor' — only affects empty-state copy. */
  kind?: string;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (venue: Venue): boolean => venue.status === STATUSES.DISABLED;

const isPublic = (venue: Venue): boolean => venue.visibility === "public" && venue.reviewStatus === "approved";

const categoryNames = (venue: Venue): string[] =>
  (venue.categories ?? [])
    .map((c: VenueCategory) => c.name || c.code || "")
    .filter(Boolean);

// A compact one-line summary of the venue's primary address for the list.
const primaryAddressLine = (venue: Venue): string => {
  const a = venue.primaryAddress;

  if (!a) {
    return "";
  }

  return [a.streetAddress, a.city, a.stateProvince, a.country]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(", ");
};

// A Google Maps link for the address: precise pin when coordinates exist, else a
// text search of the formatted address.
const mapUrl = (venue: Venue): string | null => {
  const a = venue.primaryAddress;

  if (!a) {
    return null;
  }

  if (typeof a.latitude === "number" && typeof a.longitude === "number") {
    return `https://www.google.com/maps/search/?api=1&query=${a.latitude},${a.longitude}`;
  }

  const line = primaryAddressLine(venue);

  return line ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(line)}` : null;
};

const PrimaryAddressLine: React.FC<{ venue: Venue }> = ({ venue }) => {
  const line = primaryAddressLine(venue);

  if (!line) {
    return null;
  }

  const url = mapUrl(venue);

  return (
    <Group gap={4} wrap="nowrap" mt={2} style={{ minWidth: 0 }}>
      <AppIcon iconName="MapPin" size={12} />
      {url ? (
        <Anchor
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          size="md"
          c="dimmed"
          lineClamp={1}
          onClick={(e) => e.stopPropagation()}
        >
          {line}
        </Anchor>
      ) : (
        <Text size="xs" c="dimmed" lineClamp={1}>
          {line}
        </Text>
      )}
    </Group>
  );
};

const CategoriesBadges: React.FC<{ venue: Venue; max?: number }> = ({ venue, max = 2 }) => {
  const names = categoryNames(venue);

  if (names.length === 0) {
    return null;
  }

  const shown = names.slice(0, max);
  const rest = names.slice(max);

  return (
    <Group gap={4} wrap="wrap">
      {shown.map((name) => (
        <Badge key={name} size="xs" variant="light" color="indigo">
          {name}
        </Badge>
      ))}
      {rest.length > 0 && (
        <Tooltip label={rest.join(", ")} withArrow>
          <Badge size="xs" variant="light" color="gray">
            +{rest.length}
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
};

const StatusBadge: React.FC<{ venue: Venue }> = ({ venue }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(venue)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

const VisibilityBadge: React.FC<{ venue: Venue }> = ({ venue }) => {
  const { t } = useTranslation(["venues"]);

  if (!isPublic(venue)) {
    return null;
  }

  return (
    <Tooltip label={t("venues:visibility.publicHint", { defaultValue: "Approved & discoverable in the public directory" })} withArrow>
      <Badge size="xs" variant="outline" color="teal" leftSection={<AppIcon iconName="World" size={10} />}>
        {t("venues:visibility.public", { defaultValue: "Public" })}
      </Badge>
    </Tooltip>
  );
};

const VenueIcon: React.FC<{ venue?: Venue; size?: number }> = ({ venue, size = 34 }) => {
  const logo = venue ? venueLogoUrl(venue) : null;

  if (logo) {
    return <Avatar src={logo} alt="" size={size} radius="md" />;
  }

  return (
    <ThemeIcon variant="light" color="blue" radius="xl" size={size}>
      <AppIcon iconName="BuildingCommunity" size={Math.round(size * 0.55)} />
    </ThemeIcon>
  );
};

const RowActions: React.FC<{
  venue: Venue;
  onEdit?: (v: Venue) => void;
  onDelete?: (v: Venue) => void;
  onOpen?: (v: Venue) => void;
  isDeleting: boolean;
}> = ({ venue, onEdit, onDelete, onOpen, isDeleting }) => {
  const { t } = useTranslation(["common", "vendors"]);

  const showEdit = !!onEdit && recordHasRight(venue, "edit");
  const showDelete = !!onDelete && recordHasRight(venue, "remove");

  if (!showEdit && !showDelete && !onOpen) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {onOpen && (
        // #170 — a vendor's people and documents (its own page in the Library).
        <Tooltip label={t("vendors:library.open", { defaultValue: "People & documents" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="indigo"
            size="md"
            onClick={() => onOpen(venue)}
            disabled={isDeleting}
            aria-label={t("vendors:library.open", { defaultValue: "People & documents" })}
          >
            <AppIcon iconName="Certificate" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(venue)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(venue)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  venue: Venue;
  name: string;
  disabled: boolean;
  onEdit?: (v: Venue) => void;
  onDelete?: (v: Venue) => void;
  /** #170 — a vendor's people and documents. */
  onOpen?: (v: Venue) => void;
  highlighted: boolean;
  isDeleting: boolean;
  language: string;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const VenueTableRow: React.FC<RowProps> = ({
  venue,
  name,
  disabled,
  onEdit,
  onDelete,
  onOpen,
  highlighted,
  isDeleting,
  language,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => venue.id && onToggleSelect?.(venue.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <VenueIcon venue={venue} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <VisibilityBadge venue={venue} />
              <StatusBadge venue={venue} />
            </Group>
            <PrimaryAddressLine venue={venue} />
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 260 }}>
        <CategoriesBadges venue={venue} />
      </Table.Td>
      <Table.Td style={{ width: 180 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(venue.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions venue={venue} onEdit={onEdit} onDelete={onDelete} onOpen={onOpen} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const VenueTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 260 }}>
      <Skeleton height={18} width={120} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const VenueCardComponent: React.FC<CardProps> = ({
  venue,
  name,
  disabled,
  onEdit,
  onDelete,
  onOpen,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => venue.id && onToggleSelect?.(venue.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(venue, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(venue),
        disabled: isDeleting,
      });
    }

    if (onOpen) {
      actions.push({
        key: "open",
        icon: "Certificate",
        label: t("vendors:library.openShort", { defaultValue: "Documents" }),
        color: "indigo",
        onClick: () => onOpen(venue),
        disabled: isDeleting,
        secondary: true,
      });
    }

    if (onDelete && recordHasRight(venue, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(venue),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, onOpen, venue, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
            mt={4}
          />
        )}
        <VenueIcon venue={venue} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <VisibilityBadge venue={venue} />
            <StatusBadge venue={venue} />
          </Group>
          <PrimaryAddressLine venue={venue} />
          <Box mt={6}>
            <CategoriesBadges venue={venue} max={3} />
          </Box>
        </Box>
        {showActions && <RowActions venue={venue} onEdit={onEdit} onDelete={onDelete} onOpen={onOpen} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const VenueCard = React.memo(VenueCardComponent);

const VenueCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} sizes={["md", "md", "xs"]} gap={4} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const VenuesListComponent: React.FC<VenuesListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditVenue,
  onDeleteVenue,
  onOpenVenue,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddVenue,
  selection,
  selectable = false,
  kind = "venue",
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["venues", "common"]);
  const isVendor = kind === "vendor";
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onOpenVenue, onEditVenue, onDeleteVenue);

  const venuesContext = useOptionalVenuesContext();

  const venues = dataProp ?? venuesContext?.venues ?? [];
  const loading = loadingProp ?? venuesContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? venuesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? venuesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? venuesContext?.loadMore;
  const isRemoving = isRemovingProp ?? venuesContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: venues.length });

  const handleEdit = useCallback(
    (venue: Venue) => {
      closeAllCards();
      onEditVenue?.(venue);
    },
    [onEditVenue, closeAllCards]
  );

  const handleDelete = useCallback(
    (venue: Venue) => {
      closeAllCards();
      onDeleteVenue?.(venue);
    },
    [onDeleteVenue, closeAllCards]
  );

  const handleOpen = useCallback(
    (venue: Venue) => {
      closeAllCards();
      onOpenVenue?.(venue);
    },
    [onOpenVenue, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = venuesContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && venues.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {isVendor
            ? t("venues:vendor.emptyState.noMatch", { defaultValue: "No vendors match your search or filters." })
            : t("venues:emptyState.noMatch", { defaultValue: "No venues match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={
            isVendor
              ? t("venues:vendor.emptyState.title", { defaultValue: "No vendors yet" })
              : t("venues:emptyState.title", { defaultValue: "No venues yet" })
          }
          description={
            isVendor
              ? t("venues:vendor.emptyState.description", {
                  defaultValue: "Add a service provider (photographer, planner, DJ…) you work with.",
                })
              : t("venues:emptyState.description", {
                  defaultValue: "Add a venue to build your reusable venue directory.",
                })
          }
          iconName={isVendor ? "Briefcase" : "BuildingCommunity"}
          actionLabel={
            isVendor
              ? t("venues:vendor.emptyState.actionLabel", { defaultValue: "Add vendor" })
              : t("venues:emptyState.actionLabel", { defaultValue: "Add venue" })
          }
          onAction={onAddVenue}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (venue: Venue): boolean =>
    isSelectable &&
    ((!!onEditVenue && recordHasRight(venue, "edit")) || (!!onDeleteVenue && recordHasRight(venue, "remove")));

  const rowProps = (venue: Venue): RowProps => ({
    venue,
    name: venue.name || "—",
    disabled: isDisabled(venue),
    onEdit: onEditVenue ? handleEdit : undefined,
    onDelete: onDeleteVenue ? handleDelete : undefined,
    onOpen: onOpenVenue ? handleOpen : undefined,
    highlighted: venue.id === highlightedId,
    isDeleting: isRemoving(venue.id),
    language,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(venue),
    selected: selection?.isSelected(venue.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = venues
    .filter((v) => rowIsSelectable(v))
    .map((v) => v.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("venues:list.columns.name", { defaultValue: "Venue" })}</Table.Th>
                <Table.Th style={{ width: 260 }}>
                  {t("venues:list.columns.categories", { defaultValue: "Categories" })}
                </Table.Th>
                <Table.Th style={{ width: 180 }}>
                  {t("venues:list.columns.createdAt", { defaultValue: "Added" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <VenueTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : venues.map((venue) => <VenueTableRow key={venue.id} {...rowProps(venue)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <VenueCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {venues.map((venue) => (
            <motion.div
              key={venue.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <VenueCard
                {...rowProps(venue)}
                forceClose={shouldForceClose(venue.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(venue.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(venue.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const VenuesList = React.memo(VenuesListComponent);
